import { useState, useContext } from 'react'
import styles from './styles.module.css'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import WODetailsSubModal from '../../sub-modals/WODetailsSubModal'
import ScheduleContainerModal from '../../scheduling-modal/container-modal/WorkOrderScheduleContainerModal'
import { WoDetailContext } from '../../../../context/context'
import {
  MODES,
  SCHEDULING_KEYS,
} from '../../../../../../global/constants/scheduler'
import AddRescheduledReason from '../../sub-modals/modal-content/rescheduled/AddRescheduledReason'
import { useNavigationEffect } from './hooks/useNavigationEffect'
import {
  CANCELLED_STATUSES,
  STATUSES as ORDER_STATUSES,
} from '../../../../../../global/constants/order-status'
import {
  STATUSES as INVOICE_STATUSES,
  TYPES,
} from '../../../../../../global/constants/invoice'
import { ROUTING_ALGORITHMS } from '../../../../../../global/constants/scheduling'
import { fetchData } from '../../../../../../global/utils/fetch'
import { updateOrderStatusToPendingRescheduling } from '../../../../api'
import { SUB_MODAL_NAMES } from '../../sub-modals/constants'
import isTransactionSettled from '../../../../../../global/utils/invoice/status/is-transaction-settled'
import isTransactionProcessing from '../../../../../../global/utils/invoice/status/is-transaction-processing'
import { getAllTireRackOrderNumbers } from '../../../../../../global/utils/tire-rack-orders'

export interface ITrOrderNumbersProps {
  isReadOnly: boolean
  trOrderNum?: string
  addTrOrderNum?: string
}

export default function OrderActions({ isReadOnly }: ITrOrderNumbersProps) {
  const { data: order } = useContext(WoDetailContext)
  const [tireRackOrderNumberInput, setTireRackOrderNumberInput] = useState('')
  const [deleteTireRackOrderValue, setDeleteTireRackOrderValue] = useState('')
  const tireRackOrderNumbers = getAllTireRackOrderNumbers(order)

  //scheduler modal
  const [scheduleModalOpen, setScheduleModalOpen] = useState<boolean>(false)
  const [disableSchedulerNavigation, setDisableSchedulerNavigation] =
    useState<boolean>(false)
  const [addRescheduleReasonModalOpen, setAddRescheduleReasonModalOpen] =
    useState<boolean>(false)
  const [currentSchedulingPage, setCurrentSchedulingPage] =
    useState<SCHEDULING_KEYS>(SCHEDULING_KEYS.customerInformation)
  const [currentWorkOrderSubModalPage, setCurrentWorkOrderSubModalPage] =
    useState<string>('')
  const [rescheduleReason, setRescheduleReason] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  //mode: controls the flow depending on the button clicked
  //e.g. if "finish" is clicked, the modal will navigate through the whole scheduling flow instead of closing after saving the customer info
  const [mode, setMode] = useState<MODES>(MODES.EDIT)

  function handleRescheduleClick() {
    if (order.status !== ORDER_STATUSES.reschedule) {
      handleOpenAddRescheduleReason()
    } else {
      // this is where the status is already "pending_rescheduling" so we can skip the reschedule reason
      setCurrentSchedulingPage(SCHEDULING_KEYS.appointmentSlot)
      setMode(MODES.EDIT)
      setDisableSchedulerNavigation(true)
      setScheduleModalOpen(true)
    }
  }

  function handleOpenAddRescheduleReason() {
    setAddRescheduleReasonModalOpen(true)
  }
  function handleAddRescheduleReasonCanceled() {
    setAddRescheduleReasonModalOpen(false)
  }
  async function handleAddRescheduleReasonConfirmed(reason: string) {
    setIsLoading(true)
    setRescheduleReason(reason)
    if (order.location?.routingAlgorithm === ROUTING_ALGORITHMS.GREEDY) {
      // we must first update the order status to pending rescheduling
      // in order for the calendar to show the correct availability
      await fetchData<void>(
        updateOrderStatusToPendingRescheduling(order.objectId, reason),
      )
    } else {
      throw new Error(
        `Routing algorithm ${order.location?.routingAlgorithm} is not supported`,
      )
    }
    setAddRescheduleReasonModalOpen(false)
    setCurrentSchedulingPage(SCHEDULING_KEYS.appointmentSlot)
    setDisableSchedulerNavigation(true)
    setScheduleModalOpen(true)
    setIsLoading(false)
  }
  function handleClosedScheduler() {
    setScheduleModalOpen(false)
  }

  function isOrderCompleted(): boolean {
    return order.status === ORDER_STATUSES.completed
  }
  function shouldDisableEditOrder(): boolean {
    if (isReadOnly) {
      return true
    }

    if (
      order.status === ORDER_STATUSES.cancelled_onsite ||
      order.status === ORDER_STATUSES.cancelled
    ) {
      return true
    }

    let shouldDisableEditOrder = false
    if (order.invoices.length) {
      const hasSettledInvoice = Boolean(
        order.invoices.find(
          (invoice) =>
            !invoice.tip &&
            invoice.type === TYPES.COMPLETED &&
            isTransactionSettled(invoice),
        ),
      )
      const hasProcessingInvoice = Boolean(
        order.invoices.find(
          (invoice) =>
            !invoice.tip &&
            invoice.type === TYPES.COMPLETED &&
            isTransactionProcessing(invoice),
        ),
      )
      const hasFreeInvoice = Boolean(
        order.invoices.find(
          (invoice) => !invoice.tip && invoice.status === INVOICE_STATUSES.FREE,
        ),
      )
      shouldDisableEditOrder =
        hasProcessingInvoice ||
        hasSettledInvoice ||
        (hasFreeInvoice && isOrderCompleted())
    }

    return shouldDisableEditOrder
  }

  //WODetailsSubModal
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  function handleOpenSubModal(subModalName: string) {
    setCurrentWorkOrderSubModalPage(subModalName)
    setModalOpen(true)
  }

  function handleDeleteTireRackOrderNumber(tireRackOrderNumber: string) {
    setCurrentWorkOrderSubModalPage(SUB_MODAL_NAMES.REMOVE_TIRE_RACK_ORDER)
    setDeleteTireRackOrderValue(tireRackOrderNumber)
    setModalOpen(true)
  }
  function handleCloseSubModal() {
    setModalOpen(false)
    setDeleteTireRackOrderValue('')
  }
  function finishTireRackOrderHandler() {
    setMode(MODES.FINISH)
    setCurrentSchedulingPage(SCHEDULING_KEYS.customerInformation)
    setScheduleModalOpen(true)
  }

  /* Finish button should be enabled for
   *  - 'quote's w/ TR order
   *  - any clone in 'quote'
   */

  const shouldEnableFinishOrderButton =
    !isReadOnly &&
    order.status === ORDER_STATUSES.quote &&
    (Boolean(order.tireRackOrders) || order.createdFrom)
  const shouldDisableReschedule =
    isReadOnly ||
    [...CANCELLED_STATUSES, ORDER_STATUSES.completed].includes(
      order.status ?? '',
    )

  //custom hook to open finish flow when redirected here from cloning order
  useNavigationEffect(order, finishTireRackOrderHandler)

  const shouldDisableCancelOrder =
    isReadOnly ||
    [...CANCELLED_STATUSES, ORDER_STATUSES.completed].includes(
      order.status ?? '',
    )

  return (
    <div className={styles.orderActionsContainer}>
      <TextField
        label='Add TR Order Number'
        value={tireRackOrderNumberInput}
        onChange={(e) =>
          setTireRackOrderNumberInput(e.target.value.toUpperCase())
        }
        InputLabelProps={{ shrink: true }}
        sx={{ width: '250px', marginRight: '8px' }}
        disabled={isReadOnly}
        inputProps={{
          style: { textTransform: 'uppercase' },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <button
                className={styles.inputButton}
                onClick={() => handleOpenSubModal(SUB_MODAL_NAMES.ASSIGN)}
                disabled={isReadOnly}
              >
                {SUB_MODAL_NAMES.ASSIGN}
              </button>
              <div className={styles.line}></div>
              <button
                className={styles.inputButton}
                disabled={!shouldEnableFinishOrderButton}
                onClick={finishTireRackOrderHandler}
              >
                Finish
              </button>
            </InputAdornment>
          ),
        }}
      />
      <div className={styles.trOrderNumbersContainer}>
        {(tireRackOrderNumbers || []).map((trOrderNum, index) => (
          <div
            key={index}
            className={styles.trOrderNumberWrapper}
          >
            <span className={styles.trOrderNumber}>{trOrderNum}</span>
            {!isReadOnly && (
              <button
                className={styles.deleteButton}
                onClick={() => handleDeleteTireRackOrderNumber(trOrderNum)}
              >
                X
              </button>
            )}
          </div>
        ))}
      </div>
      <div className={styles.additionalActions}>
        <WODetailsSubModal
          isOpen={modalOpen}
          handleClosed={handleCloseSubModal}
          actionName={currentWorkOrderSubModalPage}
          trNumber={deleteTireRackOrderValue || tireRackOrderNumberInput}
        />
        <AddRescheduledReason
          isOpen={addRescheduleReasonModalOpen}
          handleCancel={handleAddRescheduleReasonCanceled}
          handleConfirm={handleAddRescheduleReasonConfirmed}
          isLoading={isLoading}
        />
        <ScheduleContainerModal
          isOpen={scheduleModalOpen}
          handleClosed={handleClosedScheduler}
          currentPage={currentSchedulingPage}
          setCurrentPage={setCurrentSchedulingPage}
          mode={mode}
          disableSchedulerNavigation={disableSchedulerNavigation}
          rescheduleReason={rescheduleReason}
          isOrderCompleted={isOrderCompleted()}
        />
        <button
          className={styles.button}
          disabled={shouldDisableEditOrder()}
          onClick={() => {
            setCurrentSchedulingPage(
              isOrderCompleted()
                ? SCHEDULING_KEYS.paymentInformation
                : SCHEDULING_KEYS.customerInformation,
            )
            setMode(MODES.EDIT)
            setDisableSchedulerNavigation(false)
            setScheduleModalOpen(true)
          }}
        >
          Edit Order
        </button>
        <button
          className={styles.button}
          onClick={() =>
            handleOpenSubModal(SUB_MODAL_NAMES.SEND_CUSTOMER_CC_UPDATE)
          }
          disabled={isReadOnly}
        >
          {SUB_MODAL_NAMES.SEND_CUSTOMER_CC_UPDATE}
        </button>
        <button
          className={styles.button}
          onClick={() => {
            handleRescheduleClick()
          }}
          disabled={shouldDisableReschedule}
        >
          Reschedule
        </button>
        <button
          className={styles.button}
          onClick={() => handleOpenSubModal(SUB_MODAL_NAMES.CANCEL_ORDER)}
          disabled={shouldDisableCancelOrder}
        >
          {SUB_MODAL_NAMES.CANCEL_ORDER}
        </button>
      </div>
    </div>
  )
}
